// src/components/NewEntryForm.js
import React, { useState } from 'react';
import { formatDate, formatDateForAPI } from '../utils/dateUtils';

const NewEntryForm = ({ auftraege, stundenarten, onSubmit, onCancel, selectedDate, auftragUsage = {} }) => {
  // Berechne das minimale und maximale Datum
  const today = new Date();
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(today.getDate() - 10);
  
  const maxDate = formatDateForAPI(today);
  const minDate = formatDateForAPI(tenDaysAgo);

  const [formData, setFormData] = useState({
    stuaufidref: '',
    stusaridref: '1',  // Default-Wert
    studatum: selectedDate ? formatDateForAPI(new Date(selectedDate)) : formatDateForAPI(new Date()),
    stuanzahl: '',
    stubeschrieb: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    try {
      // Validierung
      if (!formData.stuaufidref || !formData.stusaridref || !formData.studatum || !formData.stuanzahl) {
        throw new Error('Bitte füllen Sie alle Pflichtfelder aus.');
      }

      // Prüfe ob das Datum im erlaubten Bereich liegt
      const selectedDate = new Date(formData.studatum);
      if (selectedDate < tenDaysAgo || selectedDate > today) {
        throw new Error('Das Datum muss innerhalb der letzten 10 Tage liegen.');
      }

      onSubmit({
        stuaufidref: parseInt(formData.stuaufidref),
        stusaridref: parseInt(formData.stusaridref),
        studatum: formData.studatum,
        stuanzahl: parseFloat(formData.stuanzahl),
        stubeschrieb: formData.stubeschrieb
      });
      
      // Formular zurücksetzen, aber Datum beibehalten
      setFormData(prev => ({
        stuaufidref: '',
        stusaridref: '1',
        studatum: prev.studatum,
        stuanzahl: '',
        stubeschrieb: ''
      }));
    } catch (error) {
      console.error('Formularfehler:', error);
      alert(error.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    fontSize: '16px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    boxSizing: 'border-box'
  };

  return (
    <form onSubmit={handleSubmit} className="new-entry-form" style={{ maxWidth: '450px' }}>
      {/* Zeile 1: Datum + Stunden */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
        <div style={{ flex: '1 1 60%' }}>
          <label htmlFor="studatum">Datum:</label>
          <input
            type="date"
            id="studatum"
            name="studatum"
            value={formData.studatum}
            onChange={handleChange}
            min={minDate}
            max={maxDate}
            required
            style={inputStyle}
          />
        </div>

        <div style={{ flex: '1 1 40%' }}>
          <label htmlFor="stuanzahl">Stunden:</label>
          <input
            type="number"
            id="stuanzahl"
            name="stuanzahl"
            value={formData.stuanzahl}
            onChange={handleChange}
            step="0.05"
            min="0"
            required
            style={inputStyle}
          />
        </div>
      </div>

      {/* Zeile 2: Auftrag + Art */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
        <div style={{ flex: '1 1 60%' }}>
          <label htmlFor="stuaufidref">Auftrag:</label>
          <select
            id="stuaufidref"
            name="stuaufidref"
            value={formData.stuaufidref}
            onChange={handleChange}
            required
            style={inputStyle}
          >
            <option value="">Bitte wählen...</option>
            {(() => {
              const activeAuftraege = auftraege.filter(a => !a.AufAbschluss);
              const frequent = activeAuftraege
                .filter(a => auftragUsage[a.AufID] > 0)
                .sort((a, b) => (auftragUsage[b.AufID] || 0) - (auftragUsage[a.AufID] || 0))
                .slice(0, 12)
                .sort((a, b) => String(a.AufNr).length - String(b.AufNr).length || String(a.AufNr).localeCompare(String(b.AufNr)));
              const frequentIds = new Set(frequent.map(a => a.AufID));
              const rest = activeAuftraege
                .filter(a => !frequentIds.has(a.AufID))
                .sort((a, b) => String(a.AufNr).length - String(b.AufNr).length || String(a.AufNr).localeCompare(String(b.AufNr)));
              return (
                <>
                  {frequent.length > 0 && (
                    <>
                      <option disabled>── Häufig verwendet ──</option>
                      {frequent.map(auftrag => (
                        <option key={auftrag.AufID} value={auftrag.AufID}>
                          {auftrag.AufNr} - {auftrag.AufBez}
                        </option>
                      ))}
                    </>
                  )}
                  {rest.length > 0 && (
                    <>
                      <option disabled>── Alle Aufträge ──</option>
                      {rest.map(auftrag => (
                        <option key={auftrag.AufID} value={auftrag.AufID}>
                          {auftrag.AufNr} - {auftrag.AufBez}
                        </option>
                      ))}
                    </>
                  )}
                </>
              );
            })()}
          </select>
        </div>

        <div style={{ flex: '1 1 40%' }}>
          <label htmlFor="stusaridref">Art:</label>
          <select
            id="stusaridref"
            name="stusaridref"
            value={formData.stusaridref}
            onChange={handleChange}
            required
            style={inputStyle}
          >
            <option value="">Bitte wählen...</option>
            {stundenarten.map(art => (
              <option key={art.Sart} value={art.Sart}>
                {art.SarBezeichnung}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Zeile 3: Beschreibung */}
      <div style={{ marginBottom: '12px' }}>
        <label htmlFor="stubeschrieb">Beschreibung:</label>
        <textarea
          id="stubeschrieb"
          name="stubeschrieb"
          value={formData.stubeschrieb}
          onChange={handleChange}
          rows="2"
          style={inputStyle}
        />
      </div>

      {/* Buttons nebeneinander auf gleicher Höhe */}
      <div style={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
        <button
          type="submit"
          style={{
            flex: 1,
            padding: '12px 20px',
            fontSize: '16px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            height: '48px'
          }}
        >
          Speichern
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            flex: 1,
            padding: '12px 20px',
            fontSize: '16px',
            backgroundColor: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            height: '48px'
          }}
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
};

export default NewEntryForm;